/**
 * DAY / NIGHT FOR THE GROCERY CUSTOMER SURFACE.
 *
 * REUSED, NOT REINVENTED: the portal already owns one theme system —
 * `lib/ThemeContext.jsx` toggles the `dark` class on <html> and remembers a
 * light/dark choice in localStorage under `yulontap-theme-preference`. Tailwind
 * is configured for that class and every grocery component already carries its
 * `dark:` variants, so this module adds only the three things a shared customer
 * device needs and the portal system has not got:
 *
 *   1. AUTO, which follows the phone's own setting and keeps following it;
 *   2. a choice remembered PER MEMBER on the device (a fridge card is shared,
 *      so one member must not overwrite another's choice);
 *   3. a clean hand-back: a staff member who wanders onto a grocery page gets
 *      their portal theme back when they leave.
 *
 * ONE PIECE OF STATE, SHARED. The header control and the demo-bar control are
 * two views of the same thing, so the mode lives in this module and every
 * subscriber re-renders together. It writes only its own keys, never the
 * portal's, and it paints the same `dark` class the portal paints.
 */
import { useCallback, useEffect, useState } from 'react';

export const MODES = ['auto', 'day', 'night'];
const PORTAL_KEY = 'yulontap-theme-preference';
export const groceryModeKey = (member) => `yot.grocery.mode:${member || 'fridge'}`;

export const systemIsDark = () => {
    try {
        return !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    } catch (_e) { return false; }
};

export const readMode = (member) => {
    try {
        const v = localStorage.getItem(groceryModeKey(member));
        return MODES.includes(v) ? v : 'auto';
    } catch (_e) { return 'auto'; }
};

export const isDarkFor = (mode) => (mode === 'night' || (mode === 'auto' && systemIsDark()));

const paint = (dark) => { document.documentElement.classList.toggle('dark', !!dark); };

const restorePortal = () => {
    let pref = 'light';
    try { pref = localStorage.getItem(PORTAL_KEY) || 'light'; } catch (_e) { /* private mode */ }
    paint(pref === 'dark');
};

// ── the one shared piece of state ─────────────────────────────────────────
const firstMode = () => { const own = readMode('fridge');
    return own === 'auto' ? readMode('last') : own; };
const store = { member: 'fridge', mode: firstMode(), subs: new Set(), mounted: 0 };

const publish = () => { store.subs.forEach((fn) => fn()); };

const apply = () => { paint(isDarkFor(store.mode)); };

export const setMode = (next) => {
    store.mode = MODES.includes(next) ? next : 'auto';
    try {
        localStorage.setItem(groceryModeKey(store.member), store.mode);
        // A DEVICE-LEVEL FALLBACK, so the viewer's choice outlives a Reset Demo
        // (which re-creates the demo members with fresh ids) and the moment
        // before the list has told us who is holding the card.
        localStorage.setItem(groceryModeKey('last'), store.mode);
    } catch (_e) { /* ignore */ }
    apply();
    publish();
};

export const setMember = (member) => {
    const key = member || 'fridge';
    if (store.member === key) return;
    let stored = null;
    try { stored = localStorage.getItem(groceryModeKey(key)); } catch (_e) { /* ignore */ }
    store.member = key;
    if (MODES.includes(stored)) {
        store.mode = stored;
    } else {
        // A member who has never chosen KEEPS WHAT IS ON SCREEN. Snapping back
        // to Auto here made the page flicker the moment the list told us who
        // was holding the card, and lost the choice across a Reset Demo.
        try { localStorage.setItem(groceryModeKey(key), store.mode); } catch (_e) { /* ignore */ }
    }
    apply();
    publish();
};

/** `memberKey` is passed by the surface that KNOWS who is holding the card;
 *  a control that does not know it (the demo bar) simply subscribes. */
export function useGroceryMode(memberKey) {
    const [, bump] = useState(0);

    useEffect(() => {
        if (memberKey) setMember(memberKey);
    }, [memberKey]);

    useEffect(() => {
        const fn = () => bump((n) => n + 1);
        store.subs.add(fn);
        store.mounted += 1;
        document.documentElement.classList.add('grocery-surface');
        apply();
        const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
        const onSystem = () => { if (store.mode === 'auto') apply(); };
        mq?.addEventListener?.('change', onSystem);
        return () => {
            store.subs.delete(fn);
            store.mounted -= 1;
            mq?.removeEventListener?.('change', onSystem);
            // The LAST grocery surface to leave hands the page back.
            if (store.mounted <= 0) {
                document.documentElement.classList.remove('grocery-surface');
                restorePortal();
            }
        };
    }, []);

    const cycle = useCallback(() => {
        setMode(MODES[(MODES.indexOf(store.mode) + 1) % MODES.length]);
    }, []);

    return { mode: store.mode, choose: setMode, cycle, dark: isDarkFor(store.mode) };
}
